<template>
    <div>
        <div class="g-mb-5">
            <component :is="isDeclined ? 'span' : 'a'" class="image campaign-image is-4by3" @click="goToActivation">
                <!-- Could replace with GThumbnailComponent, but needs to match CampaignListRow -->
                <img class="activation-image" :src="activationImage" :alt="brandName" />
            </component>
        </div>
        <div>
            <div class="g-mb-3">
                <brief-status :show-new="true" :brief="brief" />
            </div>
            <g-typography
                class="campaign-title g-mb-2"
                tag="h2"
                font-weight="semibold"
                :value="activation.public_name"
            />
            <g-typography v-if="activation.description" class="break-new-line g-mb-3" tag="div" color="grey-6">
                <div class="preview-div">
                    <div v-html="activation.description"></div>
                </div>
            </g-typography>
            <div v-if="rowDate" class="g-mb-3">
                <g-typography tag="span" color="grey-6" :value="getCustomizableTitle(rowDate.label)" />
                <g-typography tag="span" color="grey-6" :value="formatDate(rowDate.date)" />
            </div>
            <div class="g-mb-3 g-mt-5">
                <g-button v-if="shouldDisplayCreateProposalButton" shape="round" theme="live" :type="isNew && !isActivationArchived ? 'primary' : 'default'" @click="createProposal">
                        Create Proposal
                    </g-button>

                <router-link v-if="!isDeclined" :to="continueRoute" class="no-underline">
                    <g-button v-if="!isFlagReocurringProposalsOn" shape="round" theme="live" :type="isNew && !isActivationArchived ? 'primary' : 'default'">
                        {{ continueText }}
                    </g-button>

                    <g-button v-if="isFlagReocurringProposalsOn" shape="round" theme="live" :type="isNew && !isActivationArchived ? 'primary' : 'default'">
                        {{ activationHasMultipleBriefs ? 'View Proposals' : 'View' }}
                    </g-button>
                </router-link>
            </div>
        </div>
    </div>
</template>

<script>
    import get from 'lodash/get';
    import moment from 'moment-timezone';
    import {
        ACTIVATION_BRIEF_STATUS_NONE,
        ACTIVATION_BRIEF_STATUS_IN_REVIEW,
        ACTIVATION_BRIEF_STATUS_PENDING_APPROVAL,
        ACTIVATION_BRIEF_STATUS_ACCEPTED,
        ACTIVATION_BRIEF_STATUS_DECLINED,
        ACTIVATION_BRIEF_STATUS_COMPLETE
    } from '../../../../app-v2/constants/activation-brief';
    import { getActivationCustomizableTitle } from '../../../../app-v2/helpers/activations/default-texts';
    import { ACTIVATION_STATUS_ARCHIVED } from '../../../../app-v2/constants/activation';
    import BriefStatus from '../../../../app-v2/components/library/BriefStatus.vue';
    import ActivationBriefMixin from '../../../../app-v2/components/providers/mixins/ActivationBriefMixin';

    export default {
        name: 'ActivationListItem',

        components: {
            BriefStatus
        },

        mixins: [
            ActivationBriefMixin
        ],

        props: {
            type: {
                type: String,
                required: true
            },
            object: {
                type: Object,
                required: true
            },
            hasReocurringProposals: {
                type: Boolean,
                required: false
            }
        },

        data() {
            return {
                showFullDescription: false
            };
        },

        computed: {
            isFlagReocurringProposalsOn() {
                return this.hasReocurringProposals;
            },
            isTypeActivation() {
                return this.type === 'activation';
            },
            isTypeActivationBrief() {
                return this.type === 'activation_brief';
            },
            activation() {
                return this.isTypeActivation ? this.object : get(this.object, 'activation');
            },
            brief() {
                // If this is an activation, use is_newest to ge the brief
                return this.isTypeActivationBrief
                    ? this.object
                    : get(this.object, 'briefs', []).find((brief) => brief.is_newest === true);
            },
            brandName() {
                return get(this.activation, 'brand.name', '');
            },
            shouldDisplayCreateProposalButton() {
                if (!this.isFlagReocurringProposalsOn) {
                    return false;
                }

                const brief = this.activation.briefs.find(currentBrief => currentBrief.is_newest);

                if (
                    this.isFlagReocurringProposalsOn &&
                    brief &&
                    this.activation.is_reoccurring &&
                    [ACTIVATION_BRIEF_STATUS_ACCEPTED, ACTIVATION_BRIEF_STATUS_COMPLETE, ACTIVATION_BRIEF_STATUS_DECLINED].includes(brief.status)
                ) {
                    return true;
                }

                return false;
            },
            activationHasMultipleBriefs() {
                return this.activation.briefs.filter((currentBrief) => currentBrief.status !== ACTIVATION_BRIEF_STATUS_DECLINED).length > 1;
            },
            activationImage() {
                // Default to a short but valid data URI to prevent broken images
                const defaultURI = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';

                return get(this.activation, 'image', defaultURI) ?? defaultURI;
            },
            isActivationArchived() {
                return get(this.activation, 'status') === ACTIVATION_STATUS_ARCHIVED;
            },
            isActivationReoccurring() {
                return get(this.activation, 'is_reoccurring', false);
            },
            briefProposalEnd() {
                return get(this.activation, 'brief_submission_end_at');
            },
            isNew() {
                return get(this.brief, 'status') === ACTIVATION_BRIEF_STATUS_NONE;
            },
            isPendingApproval() {
                return get(this.brief, 'status') === ACTIVATION_BRIEF_STATUS_PENDING_APPROVAL;
            },
            isDeclined() {
                return get(this.brief, 'status') === ACTIVATION_BRIEF_STATUS_DECLINED;
            },
            continueRoute() {
                if (this.isFlagReocurringProposalsOn && this.activationHasMultipleBriefs) {
                    return {
                        name: 'v2.activation.proposals',
                        params: { activation_id: this.activation.id }
                    };
                }

                if (!this.isActivationArchived && (this.isNew || this.isPendingApproval)) {
                    return {
                        name: 'v2.activation.brief',
                        params: { brief_id: this.brief.id }
                    };
                }
                return {
                    name: 'v2.activation.brief.overview',
                    params: { brief_id: this.brief.id }
                };
            },
            continueText() {
                return this.isNew && !this.isActivationArchived ? 'Get Started' : 'View';
            },
            rowDate() {
                const status = get(this.brief, 'status');

                if (!status) {
                    return null;
                }

                if (status === ACTIVATION_BRIEF_STATUS_NONE) {
                    return this.getLabeledDate(
                        this.activation.brief_submission_end_at,
                        'campaign_proposal_dates_header'
                    );
                }
                if (status === ACTIVATION_BRIEF_STATUS_IN_REVIEW) {
                    return this.getLabeledDate(this.brief.submitted_at, 'campaign_proposal_submitted_date_header');
                }
                if (status === ACTIVATION_BRIEF_STATUS_ACCEPTED) {
                    if (this.activation.content_tracking_end_at) {
                        return this.getLabeledDate(this.activation.content_tracking_end_at, 'campaign_content_dates_header');
                    }
                    return this.getLabeledDate(
                        this.brief.reviewed_at || this.brief.accepted_at,
                        'campaign_proposal_accepted_date_header'
                    );
                }
                if (status === ACTIVATION_BRIEF_STATUS_DECLINED) {
                    return this.getLabeledDate(this.brief.declined_at, 'campaign_proposal_declined_date_header');
                }

                return null;
            }
        },
        methods: {
            getCustomizableTitle(key) {
                return getActivationCustomizableTitle(this.activation, key);
            },
            formatDate(isoDate) {
                return moment.utc(isoDate).local().format('MMM D, YYYY');
            },
            getLabeledDate(date, label) {
                if (!date) {
                    return null;
                }

                return {
                    date,
                    label
                };
            },
            goToActivation() {
                if (this.isDeclined) {
                    return;
                }
                this.$router.push(this.continueRoute);
            },
            createProposal() {
                const contactId = this.$root.contact.id;
                return this.createDraftBrief(this.activation.id, contactId)
                    .then(res => {
                        return this.loadNewDraftActivationBrief(res.id, res.contact_id);
                    })
                    .then((res) => {
                        this.$router.push({
                            name: 'v2.activation.brief',
                            params: { brief_id: res.id }
                        })
                    });
            }
        }
    };
</script>

<style lang="scss" scoped>
    .break-new-line {
        white-space: pre-line;
    }
    .preview-div {
        max-height: 60px;
        text-overflow: ellipsis;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;

        ::v-deep p {
            margin-bottom: 0;
        }
    }
    .activation-image {
        object-fit: cover;
        background-color: $g-color-grey-2;
        border-radius: $g-border-radius-lg;
    }

    @media only screen and (max-width: $g-screen-size-xs) {
        button {
            width: 100%;
        }
    }
</style>
